import CloudUpload from "@mui/icons-material/CloudUpload";
import { Theme } from "@mui/system";
import { memo, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { FieldValues, useController } from "react-hook-form";
import { Else, If, Then } from "react-if";

import { Box, Typography } from "@tsp/tsp-frontend-components/mui";

import { PickByValue } from "../../types";

import Thumbnail from "./components/thumbnail";

import { AttachmentType } from "./attachment-field.enums";
import { AttachmentValue } from "./attachment-field.types";

const classes = {
  root: (filled: boolean) => ({
    padding: 1,
    borderWidth: 1,
    borderRadius: 1,
    cursor: "pointer",
    borderColor: "#c4c4c4",
    borderStyle: filled ? "solid" : "dashed",
    "&:hover": {
      borderColor: "#21293d",
    },
  }),
  placeholder: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    justifyContent: "center",
    color: theme.palette.text.disabled,
    margin: `${theme.spacing(1.25)} 0`,
  }),
  icon: {
    fill: "#E3E3E3",
    width: "35px",
    height: "35px",
  },
};

export type AttachmentFieldProps<Values extends FieldValues> = {
  name: keyof PickByValue<Values, AttachmentValue>;
  disabled?: boolean;
};

function AttachmentField<Values extends FieldValues>(props: AttachmentFieldProps<Values>) {
  const { name, disabled } = props;

  const { field } = useController<Values>({ name: name as any });

  const onDrop = useCallback(
    (files: Array<File>) => {
      field.onChange({
        file: files[0],
        type: AttachmentType.File,
      });
    },
    [field],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    disabled,
  });

  const removeFile = useCallback(() => {
    field.onChange(null);
  }, [field]);

  return (
    <Box sx={classes.root(Boolean(field.value))} {...getRootProps()} data-test-id={name}>
      <input {...getInputProps()} />
      <If condition={Boolean(field.value)}>
        <Then>
          <Box display="grid" gap={1} gridTemplateColumns="repeat(auto-fit, 128px)">
            <Thumbnail value={field.value} disabled={disabled} onRemove={removeFile} />
          </Box>
        </Then>
        <Else>
          <Box sx={classes.placeholder}>
            <Box sx={classes.icon} data-test-id="button-thumb-attachment-delete">
              <CloudUpload fontSize="large" />
            </Box>
            <Typography color="inherit" variant="body1">
              Click here to uploading images or drag&drop them here
            </Typography>
          </Box>
        </Else>
      </If>
    </Box>
  );
}

export default memo(AttachmentField) as typeof AttachmentField;
